import React, { useState, useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { toast } from 'react-toastify';
import { fetchFormData } from '../../../../Api/ApiMethods';
import { shouldToastError } from '../../../../Api/utils';
import { parseCurrencyToFloat, displayDecimal } from '../../../../_helper/regexHandler';
import { useModalCrudState } from '../../../../_helper/hooks/useModalCrudState';

const InterlocutoryRulingDataModal = ({
    apiEndpoint,
    isOpen,
    toggle,
    mode,
    selectedInterlocutoryRuling,
    refreshData,
    arbitrationId
}) => {
    const initialFormData = useMemo(() => ({
        arbitration: [],
        arrivalDate: '',
        lastDay: '',
        primaryExpertWitness: [],
        secondaryExpertWitness: [],
        expertWitnessPrice: '',
        isDeposited: false,
        comment: ''
    }), []);

    const customHandlers = useMemo(() => ({
        primaryExpertWitness: (value, prev) => ({ ...prev, primaryExpertWitness: value ? [value] : [] }),
        secondaryExpertWitness: (value, prev) => ({ ...prev, secondaryExpertWitness: value ? [value] : [] }),
        isDeposited: (value, prev) => ({ ...prev, isDeposited: value === 'true' }),
    }), []);

    const validationSchema = useMemo(() => ({
        arrivalDate: { required: true, message: 'Geliş tarihi zorunludur.' },
        lastDay: { required: true, message: 'Son gün zorunludur.' },
    }), []);

    const {
        formData,
        setFormData,
        errors,
        handleCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedInterlocutoryRuling,
        initialData: initialFormData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => {
            const existingComment = selectedItem?.comments && selectedItem.comments.length > 0
                ? selectedItem.comments[0].comment
                : '';

            return {
                ...initialData,
                ...selectedItem,
                arrivalDate: selectedItem?.arrivalDate ? new Date(selectedItem.arrivalDate).toISOString().split('T')[0] : '',
                lastDay: selectedItem?.lastDay ? new Date(selectedItem.lastDay).toISOString().split('T')[0] : '',
                primaryExpertWitness: selectedItem?.primaryExpertWitness?.id ? [selectedItem.primaryExpertWitness.id] : [],
                secondaryExpertWitness: selectedItem?.secondaryExpertWitness?.id ? [selectedItem.secondaryExpertWitness.id] : [],
                expertWitnessPrice: selectedItem?.expertWitnessPrice?.toString() || '',
                comment: existingComment,
            };
        },
        normalizeInput: true,
        customHandlers,
        validationSchema,
        submitConfig: {
            apiEndpoint,
            refreshData,
            componentName: 'Ara karar',
            buildPayload: (data) => ({
                arbitration: data.arbitration,
                arrivalDate: data.arrivalDate ? new Date(data.arrivalDate).toISOString() : null,
                lastDay: data.lastDay ? new Date(data.lastDay).toISOString() : null,
                primaryExpertWitness: data.primaryExpertWitness,
                secondaryExpertWitness: data.secondaryExpertWitness,
                expertWitnessPrice: parseCurrencyToFloat(data.expertWitnessPrice),
                isDeposited: data.isDeposited,
                comments: data.comment ? [{
                    commentType: "Genel",
                    comment: data.comment
                }] : [],
                expenses: []
            }),
        },
    });

    const [expertWitnesses, setExpertWitnesses] = useState([]);
    const expertWitnessApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expertwitness';

    useEffect(() => {
        if (isOpen) {
            fetchDropdownData();

            if (mode !== 'modify') {
                setFormData(prev => ({
                    ...prev,
                    arbitration: [arbitrationId],
                }));
            }
        }
    }, [isOpen, mode, arbitrationId, setFormData]);

    // Bilirkişi dropdown verilerini API'den çeker
    const fetchDropdownData = async () => {
        try {
            const expertWitnessResponse = await fetchFormData({
                apiEndpoint: expertWitnessApiUrl,
                page: 1,
                pageSize: 1000,
            });

            if (expertWitnessResponse?.items) {
                setExpertWitnesses(expertWitnessResponse.items);
            } else if (expertWitnessResponse?.data?.items) {
                setExpertWitnesses(expertWitnessResponse.data.items);
            } else if (Array.isArray(expertWitnessResponse)) {
                setExpertWitnesses(expertWitnessResponse);
            } else {
                setExpertWitnesses([]);
            }
        } catch (error) {
            if (shouldToastError(error)) {
                toast.error('Bilirkişi verileri yüklenirken hata oluştu');
            }
            setExpertWitnesses([]);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} size="lg">
            <ModalHeader toggle={handleCancel}>
                {mode === 'add' ? 'Ara Karar Ekle' : 'Ara Karar Düzenle'}
            </ModalHeader>
            <form>
                <ModalBody>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="arrivalDate">Geliş Tarihi <span className="text-danger">*</span></Label>
                                <Input
                                    type="date"
                                    id="arrivalDate"
                                    name="arrivalDate"
                                    value={formData.arrivalDate}
                                    onChange={handleInputChange}
                                    invalid={!!errors.arrivalDate}
                                />
                                <FormFeedback>{errors.arrivalDate}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="lastDay">Son Gün <span className="text-danger">*</span></Label>
                                <Input
                                    type="date"
                                    id="lastDay"
                                    name="lastDay"
                                    value={formData.lastDay}
                                    onChange={handleInputChange}
                                    invalid={!!errors.lastDay}
                                />
                                <FormFeedback>{errors.lastDay}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="primaryExpertWitness">Birinci Bilirkişi</Label>
                                <Input
                                    type="select"
                                    id="primaryExpertWitness"
                                    name="primaryExpertWitness"
                                    value={formData.primaryExpertWitness?.[0] || ''}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Seçiniz...</option>
                                    {expertWitnesses.map(expert => (
                                        <option key={expert.id} value={expert.id}>
                                            {expert.fullName}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="secondaryExpertWitness">İkinci Bilirkişi</Label>
                                <Input
                                    type="select"
                                    id="secondaryExpertWitness"
                                    name="secondaryExpertWitness"
                                    value={formData.secondaryExpertWitness?.[0] || ''}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Seçiniz...</option>
                                    {expertWitnesses.map(expert => (
                                        <option key={expert.id} value={expert.id}>
                                            {expert.fullName}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label for="expertWitnessPrice">Bilirkişi Ücreti</Label>
                                <Input
                                    type="text"
                                    id="expertWitnessPrice"
                                    name="expertWitnessPrice"
                                    value={displayDecimal(formData.expertWitnessPrice || '')}
                                    onChange={handleInputChange}
                                    placeholder="Örn: 1000,50"
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup className="mb-3">
                        <Row className="align-items-center">
                            <Col xl={4} md={6}>
                                <Label className="text-nowrap mb-0">Yatırıldı mı?</Label>
                            </Col>
                            <Col xl={8} md={6} className='mt-md-0 mt-2'>
                                <div className="d-flex">
                                    <div className="radio radio-primary me-3 d-flex align-items-center">
                                        <Input
                                            name="isDeposited"
                                            type="radio"
                                            value="true"
                                            id="depositedYes"
                                            checked={formData.isDeposited === true}
                                            onChange={handleInputChange}
                                        />
                                        <Label check for="depositedYes" className="ms-1 mb-0">Evet</Label>
                                    </div>
                                    <div className="radio radio-primary d-flex align-items-center">
                                        <Input
                                            name="isDeposited"
                                            type="radio"
                                            value="false"
                                            id="depositedNo"
                                            checked={formData.isDeposited === false}
                                            onChange={handleInputChange}
                                        />
                                        <Label check for="depositedNo" className="ms-1 mb-0">Hayır</Label>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </FormGroup>

                    <FormGroup>
                        <Label for="comment">Yorum</Label>
                        <Input
                            type="textarea"
                            rows="4"
                            id="comment"
                            name="comment"
                            value={formData.comment}
                            onChange={handleInputChange}
                            placeholder="Ara karar ile ilgili yorumunuzu yazınız..."
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleCancel}>İptal</Button>
                    <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                </ModalFooter>
            </form>
        </Modal>
    );
};

export default InterlocutoryRulingDataModal;
